<script>
import { debounce } from "lodash";
import ErrorMessage from "@/components/ErrorMessage";
import Pagination from "@/components/Pagination";
import Button from "@/components/Button";
import features from "@/configs/features";
import { useResourcesStore } from "../stores/resources";
import { useTownSelectionStore } from "../stores/townSelection";

const statusCodes = {
  422: "components.town_selection.422",
  404: "components.town_selection.404"
};

export default {
  setup() {
    const resourcesStore = useResourcesStore();
    const townSelectionStore = useTownSelectionStore();
    return {
      resourcesStore,
      townSelectionStore,
    }
  },
  components: {
    ErrorMessage,
    Pagination,
    Button,
  },
  props: {
    label: {
      type: String,
      default: "components.town_selection.label",
    },
    startingTowns: {
      type: Array,
      default() {
        return null;
      },
    },
    initLimit: {
      type: Number,
      default: null,
    },
    initExpanded: {
      type: Boolean,
      default: false,
    },
    initEpciMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errors: [],
      searchString: "",
      limit: features.TOWN_LIMIT ? features.TOWN_LIMIT : this.initLimit,
      epciMode: features.EPCI ? this.initEpciMode : false,
      confirmCallback: null,
      confirmEpci: null,
      expanded: this.initExpanded,
    };
  },
  computed: {
    features: () => features,
    type() {
      return this.epciMode ? "epci" : "towns";
    },
    baseStore() {
      return this.resourcesStore[this.type];
    },
    elements() {
      return this.baseStore.data;
    },
    selected() {
      return this.townSelectionStore.towns;
    },
    page() {
      return this.baseStore.page;
    },
    EPCIButtonClass() {
      return this.epciMode
        ? ["bg-white text-airbnbBlue"]
        : ["bg-airbnbBlue text-white"];
    },
  },
  async mounted() {
    this.resourcesStore.setPage({ type: this.type, page: 1 });
    this.resourcesStore.setFilter({ type: this.type, filter: "" });
    if (this.startingTowns) {
      this.townSelectionStore.setTowns(this.startingTowns);
    }
    const error = await this.resourcesStore.perPage({ type: this.type, perPage: 6 })
    if (error) {
      const {
        status,
        data: { message },
      } = error;
      this.errors.push({
        status,
        id: Date.now(),
        message: statusCodes[status] || message,
      });
    }
  },
  methods: {
    townTitle(town, isEpci = false) {
      if (!town) return null;
      if (!isEpci && town.name && town.code) {
        return `${town.name} (${town.code})`;
      }
      if (!features.INSEE && !features.DEPART) {
        return town.town;
      }
      return `${town.town} (${town.departement || town.insee})`;
    },
    closeMessage(id) {
      Object.keys(this.errors).forEach((key) => {
        if (id === this.errors[key].id) {
          this.errors.splice(key, 1);
        }
      });
    },
    isSelected({ id }) {
      return this.townSelectionStore[`${this.epciMode ? "epci" : "town"}IsSelected`](id)
    },
    toggleEPCIMode() {
      this.searchString = "";
      this.epciMode = !this.epciMode;
      this.resourcesStore.refresh({ type: this.type });
    },
    toggleTown(e) {
      if (this.townSelectionStore.townIsSelected(e.id)) {
        this.searchString = "";
      } else if (
        this.limit &&
        this.townSelectionStore.towns.length >= this.limit
      ) {
        // We're at limit, don't add any more towns
        return;
      }
      this.townSelectionStore.toggle(e);
    },
    async toggleEPCI(e) {
      const towns = await this.townSelectionStore.epciTowns(e);
      if (!this.townSelectionStore.epciIsSelected(e.id)) {
        if (towns.length === 0) {
          this.errors.push({
            id: Date.now(),
            message: "components.town_selection.error_epci",
          });
          return;
        }
        this.confirmEpci = {
          ...e,
          towns,
          add: true,
        };
        this.confirmCallback = () => {
          this.townSelectionStore.addTowns(towns);
          this.townSelectionStore.toggleEpci(e);
          this.confirmCallback = null;
        };
      } else {
        this.confirmEpci = {
          ...e,
          towns,
          add: false,
        };
        this.confirmCallback = () => {
          this.townSelectionStore.removeTowns(towns)
          this.townSelectionStore.toggleEpci(e);
          this.confirmCallback = null;
        };
      }
    },
    toggle(e) {
      if (!this.epciMode) {
        this.toggleTown(e);
      } else {
        this.toggleEPCI(e);
      }
    },
    removeTown(town) {
      this.townSelectionStore.removeTown(town);
    },
    // eslint-disable-next-line func-names
    searchQuery: debounce(async function (e) {
      const resourcesStore = useResourcesStore();
      const error = await resourcesStore.filter({
        type: this.type,
        filter: e.target.value,
      });
      if (error) {
        this.errors.push({
          id: Date.now(),
          message: error.data.message,
        });
      }
    }, 600),
    async setPage({ page } = {}) {
      const error = await this.resourcesStore.page({
        type: this.type,
        page,
      });
      if (error) {
        this.errors.push({
          id: error.headers ? error.headers.date : -1,
          message: error.data.message,
        });
      }
    },
  },
};
</script>


<template>
  <div class="w-full mb-4 select-none">
    <div v-if="confirmCallback === null">
      <button
        class="w-full py-2 px-3 appearance-none font-semibold border border-solid border-gray-300 rounded text-center text-lightGrey leading-tight hover:bg-gray-200"
        data-cy="townSelectionExpand" @click.prevent="expanded = !expanded">
        {{ $t(label) }}
        <font-awesome-icon icon="caret-down" class="inline-block text-normal" />
      </button>
      <div :class="{ hidden: !expanded }" class="overflow-y-auto break-words w-full p-1 border bg-white">
        <div v-for="msg in errors" :key="msg.id">
          <ErrorMessage :value="msg" @input="closeMessage" />
        </div>
        <div class="w-full flex flex-row">
          <form class="flex-1" @submit.prevent="">
            <input id="commune" v-model="searchString" label="" :placeholder="epciMode
              ? $t('components.town_selection.search_epci')
              : $t('components.town_selection.search_commune')
              " type="text" class="p-4 w-full" @input="searchQuery" />
          </form>
          <a v-if="features.EPCI" href="#" class="h-full p-4 border-2 mb-1 border-solid border-airbnbBlue rounded"
            :class="EPCIButtonClass" data-cy="epciToggle" @click="toggleEPCIMode">
            {{ epciMode ? $t("components.town_selection.communes") : "EPCI" }}
          </a>
        </div>
        <hr class="mx-1" />
        <div class="shadow rounded">
          <ul class="flex flex-row flex-wrap">
            <li v-for="e in elements" :key="e.id"
              class="w-1/2 p-2 py-4 uppercase hover:bg-gray-100 cursor-pointer text-base"
              :class="{ 'bg-red-200': isSelected(e) }" data-cy="searchTown" @click="toggle(e)">
              <span>{{ townTitle(e, !epciMode) }}</span>
            </li>
          </ul>
          <div class="flex justify-center">
            <Pagination v-if="page.current !== null && page.last !== null" :current="page.current" :maximum="page.last"
              @goto="setPage" />
          </div>
          <div v-if="selected.length > 0" class="w-full">
            <hr />
            <p class="p-2 text-lightGrey">
              {{ $t("components.town_selection.selected_cities") }}
            </p>
            <div data-cy="selectedTownsContainer">
              <div v-for="e in selected" :key="e.id"
                class="inline-block p-2 m-1 text-sm uppercase bg-airbnbRed rounded cursor-pointer" data-cy="selectedTown"
                @click="removeTown(e)">
                <font-awesome-icon icon="times" class="inline-block text-normal mr-2" /><span>{{ townTitle(e, !epciMode)
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="relative flex flex-col p-4 border border-solid border-gray-400 rounded">
      <button class="absolute top-0 right-0 p-4" @click="confirmCallback = null">
        <font-awesome-icon class="text-lightGrey" icon="times" />
      </button>
      <h1 class="text-xl text-lightGrey font-bold">
        {{ $t("components.town_selection.selected_epci") }}
      </h1>
      <p class="pt-4 pb-4 text-lightGrey">
        {{
          $t("components.town_selection.selected_epci_phrase", {
            name: confirmEpci.name,
            code: confirmEpci.code,
            add: confirmEpci.add
              ? $t("components.town_selection.add")
              : $t("components.town_selection.remove"),
          })
        }}
      </p>
      <div class="flex-1 pb-4 flex flex-col">
        <span v-for="town in confirmEpci.towns.slice(0, 5)" :key="town.id"
          class="mb-1 mt-1 flex-1 text-gray-800 font-bold uppercase" data-cy="epciTownDisplay">{{ townTitle(town)
          }}</span>
        <span v-if="confirmEpci.towns.length > 5" class="mt-2 text-lightGrey">{{
          $t("components.town_selection.selected_others", {
            length: confirmEpci.towns.length - 5,
          })
        }}
        </span>
      </div>
      <div class="flex flex-row justify-center items-center">
        <Button bg="airbnbBlue" class="mr-4 ml-4" data-cy="epciConfirm" @click="confirmCallback">
          {{ $t("components.town_selection.understand") }}
        </Button>
        <Button bg="airbnbOrange" class="mr-4 ml-4" data-cy="epciCancel" @click="confirmCallback = null">
          {{ $t("components.town_selection.cancel") }}
        </Button>
      </div>
    </div>
  </div>
</template>
